import { z } from 'zod';
import { Platform, Sha256, Slug, Ulid } from './common.js';
import { Library, LibraryApp, LibraryBuild, LibraryTeam } from './library.js';
/**
 * An account's Boards, the desktop apps that reach them, and the installs queued for them
 * (`docs/off-board-plan.md` §3, §4).
 *
 * Nothing of BoardFlight's runs on a Board any more, so everything the client kept on the device —
 * which build it put there, which channel it follows, the pin after a rollback — is a row on the
 * account, written by the desktop app and read by any signed-in browser. This file is the wire for
 * those rows. `install-state.ts` is the rule that reads them; `board-link.ts` is how a page asks
 * for something to happen.
 *
 * Three things bind every schema here:
 *
 * - **A Board's name is its owner's.** It is usually a Board username ("Zebra's Board"). It is
 *   returned by the `/v1/me/…` routes to the account that connected the Board and by nothing else;
 *   no team route selects it.
 * - **A Board's token is not here, in any shape.** It stays in the keychain of the computer that
 *   paired. No request carries one and no response could.
 * - **A job can only add.** There is no field on any request in this file that removes a game,
 *   unpairs a Board or launches anything (plan decision 7). Those are the desktop app's own
 *   window's to do, behind a dialog the native side draws.
 */
/* ------------------------------------------------------------------------ identity */
/**
 * What a Board is recognised by: lowercase hex SHA-256 of `/board/info`'s `model` and `name`.
 *
 * `boards.fingerprint`, the path parameter of the sync route, and the key the desktop app files a
 * Board's token under — an address changes with the router's mood, and this does not. It leaves the
 * OS version out on purpose: the legacy hash below included it, so an OS update turned one Board
 * into two.
 *
 * The same construction as {@link boardInfoHash} — the non-null parts joined by `|` — so
 * `boardFingerprint(model, name)` is exactly `boardInfoHash(model, name, null)`. Null when the
 * Board reported neither, which is a Board nothing can be filed under.
 *
 * Async because it is Web Crypto, the one digest the Worker, a browser and Node all have. The
 * Rust core computes the same value; `packages/board-fixtures` holds the vectors both are tested
 * against.
 */
export function boardFingerprint(model, name) {
    return hashIdentity([model, name]);
}
/**
 * The hash the Android client reported a Board as: `devices.board_info_hash`, and
 * `play_events.device_hash` (`BoardDeviceIdentity.kt`, `BoardInfo.identity()`).
 *
 * Byte for byte the client's: `listOfNotNull(model, name, os).joinToString("|")`, UTF-8, SHA-256,
 * lowercase hex — and null when all three are missing. **Only a null is skipped.** An empty string
 * is a part like any other, exactly as it was in Kotlin, so `("", null, null)` hashes the empty
 * string rather than answering null. Kept so a Board the account connects now can be matched to
 * the history its old reports left (`boards.board_info_hash`).
 */
export function boardInfoHash(model, name, os) {
    return hashIdentity([model, name, os]);
}
async function hashIdentity(parts) {
    const present = parts.filter((part) => part !== null && part !== undefined);
    if (present.length === 0)
        return null;
    const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(present.join('|')));
    return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
/** A {@link boardFingerprint}, as it travels: 64 lowercase hex characters. */
export const BoardFingerprint = Sha256;
/* --------------------------------------------------------------------------- limits */
/** How often a running desktop app says so (`PUT /v1/desktop`). */
export const DESKTOP_HEARTBEAT_SECONDS = 60;
/**
 * How recently a desktop must have been heard from to count as online — two missed heartbeats and
 * a little. `online` in every response below is this comparison, made by the Worker.
 */
export const DESKTOP_ONLINE_WINDOW_MS = 150_000;
/** How often a desktop with no realtime broker re-reads the queue while it runs (plan §4). */
export const BOARD_JOB_POLL_SECONDS = 30;
/** A job nobody ran is `expired` after seven days (plan §4). */
export const BOARD_JOB_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/**
 * The realtime channel an account's own nudges arrive on (plan §4, *Nudges*).
 *
 * One spelling, because three things have to agree on it: the Worker that signs a subscription,
 * the storefront and the desktop app that subscribe. An event on it carries no data — it means
 * "re-read your Boards and your queue", the rule every channel on this broker already follows.
 */
export function userRealtimeChannel(userId) {
    return `private-user-${userId}`;
}
/**
 * The one event published on {@link userRealtimeChannel}, with `{}` as its data.
 *
 * Named so a subscriber can bind to it, and for nothing else: there is one meaning on that channel
 * — re-read `GET /v1/me/boards`, the library for the Board in view, `GET /v1/me/board-jobs`, and,
 * for a desktop app, `GET /v1/desktop/update-rings` — whichever of a job, a sync, a setting, a new
 * build or an in-game update ask caused it. A subscriber that binds to every event on the channel
 * instead is equally right.
 */
export const USER_REALTIME_EVENT = 'account.changed';
/* --------------------------------------------------------------------------- desktops */
/** The three computer systems the desktop app is built for, and the only ones it is downloaded for. */
export const DesktopSystem = z.enum(['macos', 'windows', 'linux']);
/**
 * What the app is running on: one of the three {@link DesktopSystem}s, or a phone the same app was
 * built for (`docs/phone-app-plan.md` §8). A phone is never a download.
 */
export const DesktopPlatform = z.enum([...DesktopSystem.options, 'ios', 'android']);
/** Each platform's name as a person reads it — the one spelling, on a download page or in a list of devices. */
export const DESKTOP_PLATFORM_NAME = {
    macos: 'macOS',
    windows: 'Windows',
    linux: 'Linux',
    ios: 'iOS',
    android: 'Android',
};
/** Whether a platform is one of the computers, rather than a phone. */
export function isDesktopSystem(platform) {
    return DesktopSystem.safeParse(platform).success;
}
/**
 * One installed desktop app: one row per session it signed in with (`desktops`).
 *
 * `online` is the Worker's reading of `lastSeenAt` against {@link DESKTOP_ONLINE_WINDOW_MS}. A page
 * uses it for exactly one decision — whether pressing Install will show progress now or queue for
 * later — and says which, in words, either way.
 */
export const Desktop = z.object({
    id: Ulid,
    /** The computer's own name for itself, when the app could read one: "Honza's MacBook Pro". */
    name: z.string().nullable(),
    platform: DesktopPlatform.nullable(),
    /** The app's version, `1.4.0`. */
    version: z.string().nullable(),
    /** The bridge contract version it speaks (`BRIDGE_VERSION` in `board-link.ts`). */
    bridge: z.number().int().nullable(),
    /** Answered "Keep BoardFlight running" with yes — it works with its window closed (decision 10). */
    background: z.boolean(),
    lastSeenAt: z.number().int().nullable(),
    online: z.boolean(),
});
/** `GET /v1/me/desktops`. */
export const DesktopsResponse = z.object({ items: z.array(Desktop) });
/**
 * `PUT /v1/desktop` — the heartbeat, and what makes a session a desktop app's.
 *
 * Upserts the caller's own `desktops` row, keyed by its session, and stamps `lastSeenAt`. Sent at
 * start, every {@link DESKTOP_HEARTBEAT_SECONDS} while running, and whenever one of these answers
 * changes.
 */
/**
 * Why the app's own update did not take, named by what a person can do about it (`apps/desktop/src-tauri/src/update.rs`):
 *
 * - `read_only` — it runs from somewhere it cannot replace itself: the disk image, a copy macOS runs
 *   from a private place because it was opened where it was downloaded, or a read-only volume.
 *   Moving it into Applications and opening it from there is the cure.
 * - `permission` — replacing it needs an administrator, and the password was not given.
 * - `download` — the new version could not be downloaded.
 * - `signature` — the download did not match its signature, so nothing was installed.
 * - `other` — anything else; the app's log has the updater's own words.
 */
export const UpdateFailureReason = z.enum(['read_only', 'permission', 'download', 'signature', 'other']);
export const DesktopHeartbeatRequest = z.object({
    name: z.string().trim().max(120).nullish(),
    platform: DesktopPlatform,
    version: z.string().trim().min(1).max(40),
    bridge: z.number().int().min(1),
    background: z.boolean(),
    /**
     * The app's own update failed in this run, and why (`UpdateFailureReason` above), with
     * the version it was trying to reach. Absent once it has not, and from an app older than 0.1.22.
     */
    updateFailure: z.object({ reason: UpdateFailureReason, version: z.string().trim().min(1).max(40) }).nullish(),
});
/** The heartbeat's answer: the caller's own row. */
export const DesktopHeartbeatResponse = Desktop;
/* ----------------------------------------------------------------------------- Boards */
/** A Board's own name for itself, or the label its account gave it. */
export const BoardName = z.string().trim().min(1).max(120);
/**
 * Which desktop can reach a Board, and how recently it did (`desktop_boards`).
 *
 * A desktop is listed because it holds a token for the Board — it paired, or imported a pairing —
 * which is the only thing that lets it claim a job for it.
 */
export const BoardReach = z.object({
    desktopId: Ulid,
    desktopName: z.string().nullable(),
    platform: DesktopPlatform.nullable(),
    /** When that desktop last got an answer from the Board, or null if it has paired and not synced. */
    reachedAt: z.number().int().nullable(),
    /** Whether that desktop is running now ({@link Desktop.online}). Not whether the Board is on. */
    online: z.boolean(),
});
/**
 * A Board an account has connected (`boards`), as that account sees it.
 *
 * `reach` empty is a real state and the page has words for it: the Board is known — a desktop that
 * has since signed out synced it once — and nothing can install onto it until one pairs again.
 */
export const Board = z.object({
    id: Ulid,
    fingerprint: BoardFingerprint,
    /** This account's eyes only. See the header. */
    name: z.string(),
    model: z.string().nullable(),
    /** The OS version it last reported: `2.5.3`. */
    os: z.string().nullable(),
    /** Off by default (decision 9). Updates only, never a first install. */
    autoUpdate: z.boolean(),
    /** When a desktop last read what is on it. Null until the first sync. */
    lastSyncedAt: z.number().int().nullable(),
    createdAt: z.number().int(),
    reach: z.array(BoardReach),
});
/** `GET /v1/me/boards`. */
export const BoardsResponse = z.object({ items: z.array(Board) });
/**
 * `PATCH /v1/me/boards/:boardId`. Both optional, both meaningful when absent: an empty body
 * changes nothing. Answers with the {@link Board}.
 *
 * `DELETE` on the same path takes no body and answers `204`: the account forgets the Board — it is
 * listed nowhere, and its auto-update and label are cleared. The token on a desktop is the app's own
 * to drop (`bf_unpair`), and a desktop that still holds one brings the Board back with its next
 * sync, under the same id and with what it knew about the games on it.
 */
export const UpdateBoardRequest = z.object({
    autoUpdate: z.boolean().optional(),
    name: BoardName.optional(),
});
/* ------------------------------------------------------------- one game on one Board */
/**
 * A deliberate choice to stay on an older build (plan.md §9; `PinnedBuild` in `InstallState.kt`).
 *
 * After a rollback the library would otherwise say *Update available*, and one careless press
 * would undo it — which, because Board refuses a downgrade in place, cost the saved data to
 * achieve. Stored as `board_apps.pinned_build_id` + `pinned_at`; the version is the Worker's join
 * to that build, because the status rule compares versions and a build id says nothing about order.
 */
export const PinnedBuild = z.object({
    buildId: Ulid,
    versionCode: z.number().int(),
    versionName: z.string(),
    pinnedAt: z.number().int(),
});
/**
 * What is known about one game on one Board: the `board_apps` row, with the two build ids it holds
 * resolved to their versions.
 *
 * Two sources are in here and they must never be confused, which is why they are separate fields:
 *
 * - **Board's own word** — `installed`, `versionCode`, `versionName` — is what the desktop app read
 *   from `GET /v1/apps` at `seenAt`. Authoritative on presence, and on version for an APK. A web
 *   app's entry carries no version on any firmware seen (`docs/board-api.md`), so both are null.
 * - **The ledger** — `buildId`, `buildVersionCode`, `buildVersionName`, `installedAt`,
 *   `pieceModel`, `signingCertSha256` — is what BoardFlight last put there, written from that
 *   install's job report. It stands in for the version Board withholds, and it is the only thing
 *   that knows which sibling build or which signature is installed. Null `buildId` means
 *   BoardFlight has never installed this game on this Board, or the copy it installed is gone:
 *   the ledger is cleared when a sync stops listing the game.
 *
 * `channelSlug` and `pin` are this Board's preferences, set from any signed-in browser and
 * outliving an uninstall.
 */
export const BoardAppState = z.object({
    boardId: Ulid,
    appId: Ulid,
    /** Board lists it. */
    installed: z.boolean(),
    /** Board's word, an APK only. */
    versionCode: z.number().int().nullable(),
    versionName: z.string().nullable(),
    /** The ledger: the build BoardFlight last installed here. */
    buildId: Ulid.nullable(),
    /** That build's version, or null when there is no ledger or the build record is gone. */
    buildVersionCode: z.number().int().nullable(),
    buildVersionName: z.string().nullable(),
    /** When that install finished. What the settling window is measured from. */
    installedAt: z.number().int().nullable(),
    /** The Board Piece Model the installed build declared, as the build record has it. */
    pieceModel: z.string().nullable(),
    /**
     * The certificate the installed build was signed with. Null with a `buildId` is the developer's
     * own signature; null without one is "no idea" (`installedCertificateOf` in `install-state.ts`).
     */
    signingCertSha256: z.string().nullable(),
    /** The channel this Board follows for this game, or null for the game's own default. */
    channelSlug: z.string().nullable(),
    pin: PinnedBuild.nullable(),
    /** When a desktop last read the Board with this game in question. */
    seenAt: z.number().int(),
});
/**
 * `PUT /v1/me/boards/:boardId/apps/:appId` — follow a channel, pin, unpin.
 *
 * A key left out is left alone; `null` clears it — follow the game's default channel again,
 * *Follow latest again*. Works for a game that is not installed, because choosing the channel is
 * what a tester does before the first install. Answers with the {@link BoardAppState}.
 */
export const UpdateBoardAppRequest = z.object({
    channelSlug: Slug.nullable().optional(),
    pinnedBuildId: Ulid.nullable().optional(),
});
/**
 * The seven install states — the chip of plan.md §8.
 *
 * The strings are the ones `STATES` in `apps/web/src/kit/feedback.tsx` is keyed by. `installed` is
 * the Kotlin `INSTALLED_UNKNOWN_VERSION`: on the Board, at a version nobody can name.
 */
export const APP_STATUSES = [
    /** Entitled, but the channel has no ready build yet. */
    'no_build',
    'not_installed',
    /** On the Board, but at an unknown version. */
    'installed',
    'up_to_date',
    'update_available',
    /** The installed version is ahead of the newest build on this channel. */
    'newer_than_latest',
    /** Deliberately held on an older build; a newer one exists. */
    'pinned',
];
export const AppStatus = z.enum(APP_STATUSES);
/** A status with the versions it was decided from — what `evaluate` in `install-state.ts` returns. */
export const AppStatusInfo = z.object({
    status: AppStatus,
    installedVersionCode: z.number().int().nullable(),
    latestVersionCode: z.number().int().nullable(),
    /** The pin, only while it is live — the pinned build is the one actually installed. */
    pin: PinnedBuild.nullable(),
});
/**
 * One game's standing on one Board, decided — what `/v1/me/library?board=` hangs on each game.
 *
 * `status` and `headBuildId` are the Worker's call to `evaluate`; a page does not re-derive them
 * from `state` except to draw an optimistic answer between a press and the next read.
 */
export const BoardGame = z.object({
    /** Null when no desktop has ever seen this game in question on this Board. */
    state: BoardAppState.nullable(),
    /** The channel the status was judged on: this Board's choice, else the game's own. */
    channelSlug: z.string().nullable(),
    status: AppStatusInfo,
    /**
     * The build an Install or Update would put there: the head of the installed sibling for a game
     * on the Board, the channel's head for one that is not. Null when there is nothing to install.
     */
    headBuildId: Ulid.nullable(),
});
export const BoardLibraryApp = LibraryApp.extend({ board: BoardGame });
/**
 * `GET /v1/me/library?board=:boardId` — today's library, each game carrying its state on that
 * Board. Without the parameter the route answers a plain `Library`, exactly as before.
 */
export const BoardLibrary = Library.extend({
    boardId: Ulid,
    teams: z.array(LibraryTeam.extend({ apps: z.array(BoardLibraryApp) })),
});
/* -------------------------------------------------------------------------------- jobs */
/** A first install, or a newer build over one already there. The Worker decides which. */
export const BoardJobKind = z.enum(['install', 'update']);
/**
 * Where an install was asked for — `install_events.source`, and how installs are split.
 *
 * - `desktop` — pressed in the desktop app's own window.
 * - `web` — pressed in a browser, picked up by a desktop app.
 * - `auto` — nobody pressed: the Board's auto-update switch.
 * - `notice` — pressed on a game's page reached from the in-game update card's QR.
 */
export const BoardJobSource = z.enum(['desktop', 'web', 'auto', 'notice']);
export const BoardJobStatus = z.enum([
    'queued',
    'claimed',
    'downloading',
    'installing',
    'installed',
    'failed',
    'cancelled',
    'expired',
]);
/** A job a desktop may still act on. `GET /v1/me/board-jobs?active=1` is these. */
export const BOARD_JOB_ACTIVE = ['queued', 'claimed', 'downloading', 'installing'];
export function isBoardJobActive(status) {
    return BOARD_JOB_ACTIVE.includes(status);
}
/**
 * Every move a job may make, and who makes it.
 *
 * `queued → claimed` is the claim route; `queued → cancelled` is the account's `DELETE`;
 * `queued → expired` is the Worker's clock. Everything after a claim is the claiming desktop's
 * report. A move not listed is refused — in particular nothing leaves a finished state, and a
 * report cannot take a job backwards.
 *
 * `claimed → installing` skips the download: a kept artifact whose hash still matched was reused.
 * `cancelled` after a claim is the person at the desktop pressing Cancel before the upload began;
 * once the bytes are going to the Board there is nothing left to withdraw.
 *
 * `claimed → installed` and `downloading → installed` are an install whose `installing` report was
 * lost on the way. A desktop's reports are sent and not waited on, so a web game that uploads in a
 * second can finish with nothing between `downloading` and its end having arrived — and Board said
 * yes. Refusing the end it reports would leave the job to be called a failure by whoever looked
 * next. Nothing reaches `installed` from `queued`: a desktop reports only on a job it claimed.
 */
export const BOARD_JOB_TRANSITIONS = {
    queued: ['claimed', 'cancelled', 'expired'],
    claimed: ['downloading', 'installing', 'installed', 'failed', 'cancelled'],
    downloading: ['installing', 'installed', 'failed', 'cancelled'],
    installing: ['installed', 'failed'],
    installed: [],
    failed: [],
    cancelled: [],
    expired: [],
};
export function canMoveBoardJob(from, to) {
    return BOARD_JOB_TRANSITIONS[from].includes(to);
}
/**
 * Why an install cannot go in over the copy that is there (`RemoveReason` in
 * `InstallCoordinator.kt`).
 *
 * The remedy is the same for all three — remove the game, losing its saved data — and the reason
 * is not, so it travels as a word rather than being read back out of a message. A job that ends
 * this way is `failed`, and it is never retried with a removal by anything but a person, at the
 * desktop, answering a dialog (decision 7).
 *
 * - `older_build` — Board refused a lower version code in place. Its own text is in
 *   `boardMessage`.
 * - `signature_changed` — the build is signed with a different certificate than the installed
 *   copy. Predicted before a byte moved, from two things the server said; no Board text.
 * - `switch_refused` — Board refused another sibling of the installed game in place. Reacted to,
 *   never predicted; Board's text is in `boardMessage`.
 */
export const RemoveReason = z.enum(['older_build', 'signature_changed', 'switch_refused']);
/**
 * An install, wherever it was asked for (`board_jobs`).
 *
 * Every install is one of these (plan P2): the Worker writes `install_events` from its reports, so
 * a page, a desktop window and a developer's install feed describe the same attempt.
 */
export const BoardJob = z.object({
    id: Ulid,
    boardId: Ulid,
    appId: Ulid,
    /** Null until claimed, when a job asked for "the newest on the followed channel". */
    buildId: Ulid.nullable(),
    /** The resolved build's version, for a line that names it. Null with `buildId`. */
    versionName: z.string().nullable().optional(),
    versionCode: z.number().int().nullable().optional(),
    kind: BoardJobKind,
    source: BoardJobSource,
    status: BoardJobStatus,
    /** 0–1 within `downloading` or `installing`; null everywhere else. */
    progress: z.number().min(0).max(1).nullable(),
    /**
     * Why it failed — Board's own text, verbatim (`[install_failed] INSTALL_FAILED_VERSION_DOWNGRADE:
     * …`), or the pipeline's own sentence when Board never spoke (a checksum mismatch). Never
     * paraphrased; it is what `install_events.board_message` receives.
     */
    boardMessage: z.string().nullable(),
    /** Set on a `failed` job that would go through with the game removed first. */
    removeFirst: RemoveReason.nullable().optional(),
    /** The desktop that claimed it. */
    claimedBy: Ulid.nullable(),
    createdAt: z.number().int(),
    claimedAt: z.number().int().nullable(),
    finishedAt: z.number().int().nullable(),
    expiresAt: z.number().int(),
});
/**
 * `POST /v1/me/board-jobs` → {@link BoardJob}.
 *
 * Entitlement is checked against `appId` and, when given, `buildId`. With no `buildId` the job
 * means "the newest build on the channel this Board follows", resolved when a desktop claims it —
 * so a job queued from a phone on Monday installs Wednesday's build on Wednesday.
 *
 * `kind` is not asked for: whether this is an install or an update is a fact about the Board, and
 * the Worker has the row. There is no `removeFirst`, by design and not by omission (decision 7).
 */
export const CreateBoardJobRequest = z.object({
    boardId: Ulid,
    appId: Ulid,
    buildId: Ulid.optional(),
    source: BoardJobSource,
});
/** `GET /v1/me/board-jobs?board=&active=1` — the queue, newest first. */
export const BoardJobsQuery = z.object({
    board: Ulid.optional(),
    /** `1` for only the jobs something may still happen to ({@link BOARD_JOB_ACTIVE}). */
    active: z.enum(['0', '1']).optional(),
});
export const BoardJobsResponse = z.object({ items: z.array(BoardJob) });
/* ------------------------------------------------------------- the desktop app only */
/**
 * One game the desktop app found on a Board, in a sync.
 *
 * **Entitled games only.** The app matches Board's list against the account's library — an APK by
 * `packageName`, a web app by `packageId`, folded (`indexInstalledApps` in `install-state.ts`) — and
 * sends what matched. A game the account has no right to never leaves the computer, and neither
 * does anything else on the Board.
 */
export const BoardSyncApp = z.object({
    appId: Ulid,
    /** Board's word. Null for a web app, whose entry has no version. */
    versionCode: z.number().int().nullish(),
    versionName: z.string().trim().max(120).nullish(),
});
/**
 * `PUT /v1/desktop/boards/:fingerprint` — the sync. A session with a `desktops` row only.
 *
 * The desktop app sends what it just read; the Worker upserts `boards`, `board_apps` and the
 * caller's `desktop_boards` row, and diffs against what was there to write presence.
 *
 * **`apps` is the whole list, so it is only ever sent after a successful, authenticated
 * `GET /v1/apps`.** A game missing from it is recorded as no longer installed. A desktop that could
 * not read the Board — unreachable, a `401`, a body it could not parse — sends nothing at all:
 * "Board says nothing is installed" and "nobody asked the Board" are different facts, and sending
 * the second as the first reads as every game having just been uninstalled.
 */
export const BoardSyncRequest = z.object({
    /** The Board's own name. A Board that reports none is sent under its model, then as "Board". */
    name: BoardName,
    model: z.string().trim().max(80).nullish(),
    os: z.string().trim().max(40).nullish(),
    /** {@link boardInfoHash} of the same three fields, so old reports can be joined to this Board. */
    boardInfoHash: Sha256.nullish(),
    apps: z.array(BoardSyncApp).max(1000),
});
/**
 * An update the Worker found waiting on a Board whose auto-update is on.
 *
 * Already filtered by every rule that needs the account's data: updates only, inside the installed
 * sibling, not pinned, and a build this Board has not been offered before. What is left to the
 * desktop is what only it can know — whether now is a moment to change the Board (plan §5.5).
 */
export const BoardUpdateDue = z.object({
    appId: Ulid,
    buildId: Ulid,
    versionName: z.string(),
    versionCode: z.number().int(),
    channelSlug: z.string(),
});
/** The sync's answer: the Board as the account now holds it, and what to do. */
export const BoardSyncResponse = z.object({
    board: Board,
    /** Empty unless `board.autoUpdate`. The desktop makes each a job with `source: 'auto'`. */
    due: z.array(BoardUpdateDue),
});
/**
 * `POST /v1/desktop/board-jobs/:id/claim` — `queued → claimed`, with everything the pipeline needs.
 *
 * `409` when another desktop has it or it is no longer waiting. A desktop claims only for a Board
 * it holds a token for, which the Worker knows by that desktop having synced it.
 *
 * The artifact itself is fetched the way every client fetches one, `GET /v1/builds/:id/download`
 * with the same session; a job carries no URL.
 */
export const BoardJobClaim = z.object({
    job: BoardJob,
    /** The Board's fingerprint, which is what the desktop files its token under. */
    boardFingerprint: BoardFingerprint,
    app: z.object({
        id: Ulid,
        slug: z.string(),
        name: z.string(),
        platform: Platform,
        /** What to look for in Board's list before and after — an APK's `packageName`, a web app's `packageId`. */
        packageName: z.string(),
    }),
    /** The build to install, resolved. `sha256` is verified before a byte reaches the Board. */
    build: LibraryBuild,
    /**
     * The certificate of the copy on the Board, as the ledger has it, or null for "no idea" — what
     * the signature gate compares `build.signingCertSha256` with (`signatureChanged`).
     */
    installedCertificate: z.string().nullable(),
});
/**
 * `PATCH /v1/desktop/board-jobs/:id` — a claimed job's progress and its end.
 *
 * Only the desktop that claimed it, and only along {@link BOARD_JOB_TRANSITIONS}. The Worker turns
 * these into `install_events`, each stamped with the job's `kind`: the claim wrote `started`;
 * `installing` writes `downloaded`; `installed` and `failed` write themselves, with
 * `boardMessage` — `installed` the same whether `installing` arrived before it or was lost on the
 * way. A `cancelled` job writes nothing —
 * the feed has no word for "stopped by the person", and it is left at `started`, as the client
 * left it.
 */
export const ReportBoardJobRequest = z.object({
    status: z.enum(['downloading', 'installing', 'installed', 'failed', 'cancelled']),
    progress: z.number().min(0).max(1).nullish(),
    boardMessage: z.string().max(8000).nullish(),
    /** With `failed` only. */
    removeFirst: RemoveReason.nullish(),
});
//# sourceMappingURL=boards.js.map